import React, { useMemo, useState } from 'react';
import { Icon, BandLoader } from '@cb/apricot-react';

import { StatusConfigGroup } from '../types';
import { IGetStudentPaginatedRosterData, IGetStudentTestStatusData } from '../../../../apollo/types';
import { useStateValue } from '../../../../context/AppContext';
import { buildStudentsDisplayValues } from '../../../../utils/buildStudentsDisplay';
import RosterV3 from '../../../common/roster/RosterV3';
import StudentRosterRow from '../../students/roster/StudentRosterRow';
import { DAP_STUDENT_TEST_STATUS } from '../../../../constants/bluebox';
import StatsBar from '../StatsBar';
import Spinner from '../../../ui/loading/SpinnerWrapper';
import AccordionSummary from '../../common/AccordionSummary';
import { buildTimingSummary, type TimingSummaryItem } from '../../../../utils/timingSummary';
import TestDayExamTimings from '../../common/examTimings/TestDayExamTimings';
import { CODES } from '@tdtk/types';

const baseColumns = [
  { fieldName: 'testingGroup', title: 'Testing Group', sortField: ['groupType'] },
  { fieldName: 'accommodations', title: 'Accommodations', sortField: ['accommodationsSortOrder'] },
  { fieldName: 'room', title: 'Room', sortField: ['room.title'] },
  { fieldName: 'attendance', title: 'Attendance', sortField: ['status.sortOrder'] },
  { fieldName: 'testingStatus', title: 'Testing', sortField: ['dapTestStatus.sortOrder'] },
];

const startTimeColumns = [
  { fieldName: 'startTime', title: 'Start Time', sortField: ['examStartTime'] },
  { fieldName: 'estEndTime', title: 'Est. End Time', sortField: ['estimatedExamEndTime'] },
];

const statusConfigs: StatusConfigGroup[] = [
  {
    divider: true,
    group: [
      {
        id: 'student-on-roster',
        filter: (students: IGetStudentPaginatedRosterData[]) => students,
        footer: 'Students on the roster',
      },
    ],
  },
  {
    divider: true,
    group: [
      {
        id: 'not-check-bluebook',
        filter: (students: IGetStudentPaginatedRosterData[]) =>
          students.filter((s) => s.dapTestStatus?.name === DAP_STUDENT_TEST_STATUS.NONE),
        footer: 'Not checking in to Bluebook',
      },
      {
        id: 'check-bluebook',
        filter: (students: IGetStudentPaginatedRosterData[]) =>
          students.filter((s) => s.dapTestStatus?.name === DAP_STUDENT_TEST_STATUS.EXAM_CHECKIN_STARTED),
        footer: 'Checking in to Bluebook',
      },
    ],
  },
  {
    group: [
      {
        id: 'ready-test',
        filter: (students: IGetStudentPaginatedRosterData[]) =>
          students.filter((s) => s.dapTestStatus?.name === DAP_STUDENT_TEST_STATUS.WAITING_ROOM_ARRIVAL),
        footer: 'Ready to test',
      },
      {
        id: 'started-test',
        filter: (students: IGetStudentPaginatedRosterData[]) =>
          students.filter((s) =>
            (
              [
                DAP_STUDENT_TEST_STATUS.SECTION_START_TIME,
                DAP_STUDENT_TEST_STATUS.SECTION_BREAK,
                DAP_STUDENT_TEST_STATUS.SECTION_RESUME,
                DAP_STUDENT_TEST_STATUS.STUDENT_SELF_BREAK,
                DAP_STUDENT_TEST_STATUS.EMERGENCY_BREAK,
                DAP_STUDENT_TEST_STATUS.ONLINE,
                DAP_STUDENT_TEST_STATUS.OFFLINE,
                DAP_STUDENT_TEST_STATUS.EXIT_CONFIRMED,
                DAP_STUDENT_TEST_STATUS.EXAM_SUBMISSION_PENDING,
              ] as string[]
            ).includes(s.dapTestStatus?.name)
          ),
        footer: 'Started testing',
      },
      {
        id: 'submit-answer',
        filter: (students: IGetStudentPaginatedRosterData[]) =>
          students.filter((s) => s.dapTestStatus?.name === DAP_STUDENT_TEST_STATUS.EXAM_SUBMISSION_COMPLETE),
        footer: 'Submitted answers',
      },
    ],
  },
];

function ExamTimingsSection({
  isLoading,
  assessmentId,
  tdtkAsmtType,
  showSection3Summary,
}: {
  isLoading: boolean;
  assessmentId?: string;
  tdtkAsmtType?: string;
  showSection3Summary: boolean;
}): React.ReactNode {
  if (isLoading) {
    return (
      <div className='tdtk-summary-accordion__band-loader'>
        <BandLoader />
      </div>
    );
  }
  return (
    <TestDayExamTimings
      assessmentId={assessmentId}
      tdtkAsmtType={tdtkAsmtType}
      shouldShowSection3={showSection3Summary}
    />
  );
}

interface TestDayRosterProps {
  studentsTestStatuses: IGetStudentTestStatusData[];
  allStudents: IGetStudentPaginatedRosterData[];
  lastKeyStudents?: string | null | undefined;
  lastKeyStudentTestStatuses?: string | null | undefined;
}

export function TestDayRoster({
  allStudents,
  lastKeyStudents,
  studentsTestStatuses,
  lastKeyStudentTestStatuses,
}: TestDayRosterProps) {
  const columns = [...baseColumns, ...startTimeColumns];
  const headers = [{ title: 'Student', sortField: ['candMidInit', 'candFirstName', 'candLastName'] }, ...columns];

  const { orgEvent } = useStateValue();
  const [filterId, setFilterId] = useState<string | null>(null);
  const handleTabClick = (statusId: string) => setFilterId(statusId);

  const activeStatusConfig = statusConfigs
    .map((groupConfig) => groupConfig.group)
    .flat()
    .find((config) => {
      return config.id === filterId;
    });

  const studentsDisplayValues = useMemo(() => {
    return buildStudentsDisplayValues(allStudents, studentsTestStatuses, orgEvent);
  }, [studentsTestStatuses, allStudents, orgEvent]);

  const filteredStudents = activeStatusConfig?.filter(studentsDisplayValues) ?? studentsDisplayValues;

  const timingTitle = `${orgEvent?.eventTitle || 'Event'} Timings`;
  const isInitialStudentsLoad = lastKeyStudents === undefined || lastKeyStudentTestStatuses === undefined;

  const { startSummaryText, endSummaryText } = useMemo(() => {
    const timingItems: TimingSummaryItem[] = studentsDisplayValues.map((student: IGetStudentPaginatedRosterData) => ({
      examStartTime: student.examStartTime,
      estimatedExamEndTime: student.estimatedExamEndTime,
    }));

    return buildTimingSummary(timingItems);
  }, [studentsDisplayValues]);

  const showTimingSummary = Boolean(startSummaryText || endSummaryText);

  //Determines if section 3 summary is displayed, if one student is taking the section 3 essay
  const showSection3Summary = useMemo(() => {
    return allStudents.some(
      (student) => student?.asmtSubtypeCd?.toString() === CODES.AsmtSubtypeCd.SAT_WITH_ESSAY.toString()
    );
  }, [allStudents]);

  const assessmentId = orgEvent?.asmtId;
  const tdtkAsmtType = orgEvent?.tdtkAsmtType;

  //Show loadBanner until we find a student taking section 3 essay or until we have loaded all students.
  const loadingSection3 = !showSection3Summary && lastKeyStudents !== null;

  const renderStatsBar = () => {
    return (
      <StatsBar
        lastKey={lastKeyStudentTestStatuses}
        status_config_groups={statusConfigs}
        students_status={studentsTestStatuses as IGetStudentPaginatedRosterData[]}
        filterId={filterId}
        onClick={(id: string) => handleTabClick(id)}
      />
    );
  };

  return (
    <>
      <div className='box-card py-3 px-3 mb-3'>
        <AccordionSummary
          title={
            <span className='tdtk-summary-accordion__title-content'>
              <Icon name='pending' decorative className='tdtk-summary-accordion__title-icon' />
              <h5>{timingTitle}</h5>
            </span>
          }
          startSummaryText={startSummaryText}
          endSummaryText={endSummaryText}
          showSummary={showTimingSummary}
          summaryLoading={isInitialStudentsLoad}
          panelContent={
            <ExamTimingsSection
              isLoading={loadingSection3}
              assessmentId={assessmentId}
              tdtkAsmtType={tdtkAsmtType}
              showSection3Summary={showSection3Summary}
            />
          }
        />
      </div>
      <div className='box-card py-3 px-3'>
        <div>
          {lastKeyStudents === undefined && lastKeyStudentTestStatuses === undefined ? (
            <div className='d-flex justify-content-center'>
              <Spinner />
            </div>
          ) : (
            <RosterV3<IGetStudentPaginatedRosterData>
              id='testDayRoster'
              loading={lastKeyStudents !== null}
              caption='Student Roster'
              groupName='students'
              headers={headers}
              items={filteredStudents}
              pageSize={30}
              additionalHeaderComponent={renderStatsBar}
              sortFields={[
                {
                  name: 'student',
                  order: 'asc',
                },
                {
                  name: 'status.sortOrder',
                  order: 'asc',
                },
                {
                  name: 'dapTestStatus.sortOrder',
                  order: 'asc',
                },
                {
                  name: 'candLastName',
                  order: 'asc',
                },
                {
                  name: 'candFirstName',
                  order: 'asc',
                },
                {
                  name: 'candMidInit',
                  order: 'asc',
                },
              ]}
              renderItem={(student, options) => (
                <StudentRosterRow
                  columns={columns}
                  handleCheckStudents={() => {}}
                  renderCheckBoxes={false}
                  loading={false}
                  key={`student_${student.id}`}
                  student={student}
                  options={{
                    ...options,
                    links: false,
                  }}
                />
              )}
              stickyHeaders={true}
            />
          )}
        </div>
      </div>
    </>
  );
}
